import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../../api/baseUrl";
import getDecryptedToken from "../../../../../api/getDecryptedToken";
import { ModalOverlay } from "../../../../../components/ModalOverlay";
import ClinicOrderPanels from "../../../../../components/labs/ClinicOrderPanels";
import LabOrderDetailView from "../../../../../components/labs/LabOrderDetailView";
import SendLabOrderModal from "../../../../../components/labs/SendLabOrderModal";
import type { LabOrderDetail } from "../../../../../components/labs/labOrderTypes";
import {
  buildClinicOrderPayload,
  clinicSenderDefaults,
  emptyLabOrderValues,
  orderLinesError,
  type LabOrderFormValues,
} from "../../../../../components/labs/labOrderFormUtils";
import {
  draftPhotoFailureMessage,
  uploadDraftLabOrderPhotos,
  type DraftLabOrderPhoto,
} from "../../../../../components/labs/labOrderPhotoUtils";
import { useClinicDentists } from "../../../../../components/labs/useClinicDentists";
import { useLabCatalog, useLabPriceList } from "../../../../../components/labs/useLabCatalog";
import { stageToothNos } from "./stageLabOrders";

type Partner = {
  id: string;
  labId: string | null;
  name: string;
  address?: string;
  phone?: string;
  isShadow?: boolean;
  isOwnLab?: boolean;
  canSendOrders?: boolean;
  canTrackOrders?: boolean;
  status?: string;
};

const usable = (p: Partner) =>
  Boolean((p.labId && (p.canSendOrders ?? p.status === "active")) || (p.isShadow && (p.canTrackOrders ?? p.status === "active")));

function errorText(err: unknown, fallback: string) {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
}

/**
 * A treatment stage's lab order (requirement 10): the order with its edit,
 * job card and stage panels when the stage has one, else the new-order form
 * pre-filled from the stage (patient, teeth, treatment, stage, category).
 */
export default function StageLabOrderModal({
  sheetId,
  stage,
  categoryId,
  onClose,
  onChanged,
}: {
  /** Treatment sheet id (the order's treatmentId) */
  sheetId: string;
  /** The sheet stage row (sub treatment) */
  stage: any;
  /** The stage's lab category from the catalog ("" when not flagged) */
  categoryId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const orderId: string = stage?.labOrderId ? String(stage.labOrderId?._id ?? stage.labOrderId) : "";
  const [order, setOrder] = useState<LabOrderDetail | null>(null);
  const [loading, setLoading] = useState(!!orderId);
  const [creating, setCreating] = useState(!orderId);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    setLoading(true);
    baseUrl
      .get(`client/labs/orders/${encodeURIComponent(orderId)}`)
      .then((res) => {
        if (!cancelled) setOrder(res.data.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setOrder(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (creating) {
    return (
      <StageOrderForm
        sheetId={sheetId}
        stage={stage}
        categoryId={categoryId}
        onClose={onClose}
        onCreated={() => {
          onChanged();
          onClose();
        }}
      />
    );
  }

  const stageName = [stage?.treatment, stage?.type, stage?.subtype, stage?.stage].filter(Boolean).join(" - ");

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stage-lab-order-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative flex h-[min(96dvh,960px)] w-full max-w-4xl flex-col rounded-t-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3.5 dark:border-slate-700 sm:px-6">
          <div>
            <h2 id="stage-lab-order-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Lab order · {stageName || "stage"}
            </h2>
            {order?.orderStatus === "Cancelled" ? (
              <button type="button" onClick={() => setCreating(true)} className="mt-1 text-xs font-medium text-primary hover:underline">
                This order was cancelled — create a new lab order for this stage
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-6">
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-500">Loading order…</p>
          ) : order ? (
            <>
              <ClinicOrderPanels
                order={order}
                onUpdated={(updated) => {
                  setOrder(updated);
                  onChanged();
                }}
              />
              <LabOrderDetailView order={order} />
            </>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500">Could not load the lab order.</p>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

function StageOrderForm({
  sheetId,
  stage,
  categoryId,
  onClose,
  onCreated,
}: {
  sheetId: string;
  stage: any;
  categoryId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const clinicDetails = {
    name: getDecryptedToken("clinicName") || "Clinic",
    phone: getDecryptedToken("clinicPhoneNumber") || undefined,
    address: getDecryptedToken("address") || undefined,
  };
  const [values, setValues] = useState<LabOrderFormValues>(() => ({
    ...emptyLabOrderValues(),
    ...clinicSenderDefaults(clinicDetails),
    caseTitle: [stage?.treatment, stage?.type, stage?.stage].filter(Boolean).join(" - "),
    toothNos: stageToothNos(stage?.toothNos ?? stage?.toothNo),
    caseCategoryId: categoryId,
    dentistId: stage?.didBy ? String(stage.didBy?._id ?? stage.didBy) : "",
  }));
  const [partners, setPartners] = useState<Partner[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [photos, setPhotos] = useState<DraftLabOrderPhoto[]>([]);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const { items: priceList, loading: priceListLoading } = useLabPriceList(values.labId, values.labPartnerId);
  const { catalog } = useLabCatalog();
  const dentists = useClinicDentists();

  useEffect(() => {
    baseUrl
      .get("treatment/find-treatment", { headers: { treatmentid: sheetId } })
      .then((res) => {
        const p = res.data.data?.patientId;
        const name = p?.fullName?.trim() || "";
        const regNo = p?.regNo?.trim() || "";
        setValues((prev) => ({ ...prev, patient: regNo ? `${name} (${regNo})` : name, patientMeta: regNo ? `Reg: ${regNo}` : "" }));
      })
      .catch(() => {});
    baseUrl
      .get("client/labs/partners")
      .then((res) => {
        const list: Partner[] = (res.data.data ?? []).filter(usable);
        setPartners(list);
        if (list[0]) {
          setValues((prev) =>
            prev.labPartnerId ? prev : { ...prev, labPartnerId: list[0].id, labId: list[0].labId ? String(list[0].labId) : "" }
          );
        }
      })
      .catch(() => setPartners([]));
  }, [sheetId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!values.labPartnerId) return setError("Choose a lab");
    if (!values.dueDate) return setError("Due date is required");
    if (!values.toothNos.length) return setError("Select at least one tooth");
    const problem = orderLinesError(values.invoiceItems);
    if (problem) return setError(problem);
    setSubmitting(true);
    try {
      const res = await baseUrl.post("client/labs/orders", {
        ...buildClinicOrderPayload(values, { treatmentId: sheetId }),
        stageId: String(stage._id),
      });
      const mongoId = res.data?.data?._id;
      const failed = await uploadDraftLabOrderPhotos(mongoId ? String(mongoId) : null, photos, (current, total) =>
        setProgress({ current, total })
      );
      if (failed > 0) toast.warn(draftPhotoFailureMessage(failed));
      toast.success(res.data?.message || "Lab order saved");
      onCreated();
    } catch (err) {
      setError(errorText(err, "Could not save the lab order"));
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  }

  return (
    <SendLabOrderModal
      open
      onClose={onClose}
      partners={partners.map((p) => ({
        id: p.id,
        labId: p.labId ? String(p.labId) : null,
        name: p.name,
        address: p.address,
        phone: p.phone,
        isLocal: p.isShadow,
        isOwnLab: p.isOwnLab,
      }))}
      values={values}
      onChange={setValues}
      onSubmit={submit}
      submitting={submitting}
      error={error}
      clinicDetails={clinicDetails}
      priceListItems={priceList}
      priceListLoading={priceListLoading}
      catalog={catalog}
      dentists={dentists}
      onlyCategoryId={categoryId}
      subtitle={`For stage: ${[stage?.treatment, stage?.stage].filter(Boolean).join(" - ")}`}
      photos={photos}
      onPhotosChange={setPhotos}
      photoUploadProgress={progress}
    />
  );
}
